import { useEffect, useSyncExternalStore } from 'react';

import {
    type ChipFacts,
    type ChipKind,
} from '@apps/web/features/admin_agent_dock/chip_registry';

// Tiny external store that lets a route page act as the dock's chip
// source ("context provider" role, PHO-14248). Registry route
// resolvers can only see the pathname, so they produce fallback
// labels (short ids) — or nothing at all when the route param is
// not the chip's entity id (e.g. /admin/accounts/{accountId} keys
// on the Core account id while the chip needs the linked Phoebe
// organization id). Pages that know better publish the resolved
// chip here as soon as their data lands; the dock prefers a
// published chip over the pathname resolver and rotates the
// auto-loaded chip when the published label upgrades.

export type RouteChipSourceChip = {
    readonly kind: ChipKind;
    readonly entity_id: string;
    readonly label: string;
    // Structured facts captured from the page's fast summary read;
    // persisted onto the chip so the agent kickoff carries them.
    readonly facts?: ChipFacts | null;
};

export type PublishedRouteChip = {
    // Pathname the chip was published for. The dock only honors a
    // published chip whose pathname matches its current route, so a
    // page that unmounts without cleanup cannot leak its chip onto
    // an unrelated route.
    readonly pathname: string;
    readonly chip: RouteChipSourceChip;
};

let published: PublishedRouteChip | null = null;
const listeners = new Set<() => void>();

function emit(): void {
    for (const listener of listeners) {
        listener();
    }
}

export function routeChipFactsSignature(
    facts: ChipFacts | null | undefined,
): string {
    return facts ? JSON.stringify(facts) : '';
}

export function publishRouteChip(
    pathname: string,
    chip: RouteChipSourceChip,
): void {
    if (
        published !== null &&
        published.pathname === pathname &&
        published.chip.kind === chip.kind &&
        published.chip.entity_id === chip.entity_id &&
        published.chip.label === chip.label &&
        routeChipFactsSignature(published.chip.facts) ===
            routeChipFactsSignature(chip.facts)
    ) {
        return;
    }
    published = { pathname, chip };
    emit();
}

export function clearPublishedRouteChip(pathname: string): void {
    if (published === null || published.pathname !== pathname) {
        return;
    }
    published = null;
    emit();
}

export function getPublishedRouteChip(): PublishedRouteChip | null {
    return published;
}

export function subscribeToRouteChipSource(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function usePublishedRouteChip(): PublishedRouteChip | null {
    return useSyncExternalStore(
        subscribeToRouteChipSource,
        getPublishedRouteChip,
    );
}

// Page-side publisher. Pass `null` while the page's data is still
// loading; publish fires as soon as a chip is available and clears
// on unmount or route change.
export function useRouteChipPublisher(
    pathname: string,
    chip: RouteChipSourceChip | null,
): void {
    const kind = chip?.kind ?? null;
    const entityId = chip?.entity_id ?? null;
    const label = chip?.label ?? null;
    // Facts are keyed by their serialized signature so an
    // unchanged-but-reconstructed facts object does not re-publish
    // on every render.
    const factsSignature = routeChipFactsSignature(chip?.facts);
    useEffect(() => {
        if (kind === null || entityId === null || label === null) {
            return;
        }
        publishRouteChip(pathname, {
            kind,
            entity_id: entityId,
            label,
            facts: factsSignature
                ? (JSON.parse(factsSignature) as ChipFacts)
                : null,
        });
        return () => clearPublishedRouteChip(pathname);
    }, [pathname, kind, entityId, label, factsSignature]);
}
